'use client'

import { Fragment } from 'react'
import { useIntl } from 'react-intl'
import {
  BottomSheet,
  Button,
  Cell,
  Dialog,
  Divider,
  Icon,
  Navigation,
  Spacer,
  Text,
  Validation,
} from '@vinted/web-ui'
import { InfoCircle16, X24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import {
  ReturnShippingOption,
  EscrowRefundEstimateModel,
  RefundElementsWithAction,
  EscrowRefundEstimateElementModel,
  formatCurrencyAmount,
} from '@marketplace-web/complaints/complaints-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'

type Props = {
  note?: string
  refundEstimate?: EscrowRefundEstimateModel | null
  isOpen: boolean
  isBundleComplaint?: boolean
  isSellerBusinessUser?: boolean
  selectedShippingOption?: ReturnShippingOption
  isOpenedInDialog?: boolean
  isLoading?: boolean
  onClose: () => void
  onBuyerProtectionFeeInfoClick: () => void
  onItemDiscountsInfoClick: () => void
  onCurrencyConversionInfoClick: () => void
}

const BuyerRefundSummaryModal = ({
  note,
  refundEstimate,
  isOpen,
  onClose,
  onBuyerProtectionFeeInfoClick,
  onCurrencyConversionInfoClick,
  onItemDiscountsInfoClick,
  isBundleComplaint = true,
  isSellerBusinessUser = false,
  isOpenedInDialog = false,
  selectedShippingOption,
  isLoading,
}: Props) => {
  const { locale } = useIntl()
  const translate = useTranslate('estimated_refund_dialog')
  const { phones } = useBreakpoint()
  const isMobileView = phones && !isOpenedInDialog

  const { refundElements } = refundEstimate || {}

  const buyerProtectionFeeTranslationKey = isSellerBusinessUser
    ? 'buyer_protection_pro_fee'
    : 'buyer_protection_fee'

  const renderNavigation = () => (
    <Navigation
      body={translate(selectedShippingOption ? 'confirmed_refund_title' : 'title')}
      right={
        <Button
          inline
          iconName={X24}
          styling={Button.Styling.Flat}
          onClick={onClose}
          aria={{ 'aria-label': translate('a11y.close') }}
        />
      }
    />
  )

  const getElementBody = (element: EscrowRefundEstimateElementModel) => {
    switch (element.type) {
      case RefundElementsWithAction.BuyerProtectionFee: {
        return (
          <div className="u-flexbox">
            <Text as="span" text={element.title} />
            {isBundleComplaint && (
              <>
                <Spacer orientation={Spacer.Orientation.Vertical} />
                <button
                  className="u-flexbox u-align-self-center"
                  type="button"
                  onClick={onBuyerProtectionFeeInfoClick}
                  aria-label={translate(`a11y.${buyerProtectionFeeTranslationKey}_info_icon`)}
                >
                  <Icon name={InfoCircle16} color={Icon.Color.GreyscaleLevel2} />
                </button>
              </>
            )}
          </div>
        )
      }
      case RefundElementsWithAction.TotalItemsDiscount: {
        return (
          <div className="u-flexbox">
            <Text as="span" text={element.title} />
            <Spacer orientation={Spacer.Orientation.Vertical} />
            <button
              className="u-flexbox u-align-self-center"
              type="button"
              onClick={onItemDiscountsInfoClick}
              aria-label={translate('a11y.item_discounts_info_icon')}
            >
              <Icon name={InfoCircle16} color={Icon.Color.GreyscaleLevel2} />
            </button>
          </div>
        )
      }
      default: {
        return (
          <>
            <Text as="span" text={element.title} />
            {element.error && <Validation text={element.error} theme="warning" />}
          </>
        )
      }
    }
  }

  const renderRefundElements = () => {
    if (!refundElements) return null

    return (
      <>
        {refundElements.map((element, index) => {
          const Separator = () =>
            refundElements[index + 1]?.type === 'item' ? (
              <Spacer size={Spacer.Size.Small} />
            ) : (
              <Spacer size={Spacer.Size.Large} />
            )

          return (
            <Fragment key={index}>
              <Cell
                styling={Cell.Styling.Tight}
                key={`${element.title}-${index}`}
                body={getElementBody(element)}
                suffix={formatCurrencyAmount(element.money, locale)}
              />
              {refundElements.length - 1 !== index && <Separator />}
            </Fragment>
          )
        })}
      </>
    )
  }

  const renderCurrencyConvertionNote = () => {
    if (!refundEstimate?.beforeConversion) return null

    return (
      <>
        <button type="button" onClick={onCurrencyConversionInfoClick}>
          <Text
            as="h4"
            clickable
            theme="inverse"
            type={Text.Type.Caption}
            text={translate('currency_conversion_details.learn_more_link')}
          />
        </button>
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
        <Text
          as="h4"
          type={Text.Type.Caption}
          text={translate('currency_conversion_details.learn_more_about')}
        />
      </>
    )
  }

  const renderTotalRefund = () => {
    if (!refundEstimate) return null

    return (
      <>
        <Cell
          styling={Cell.Styling.Tight}
          title={translate(selectedShippingOption ? 'confirmed_total_refund' : 'total')}
          suffix={
            <Text
              as="h2"
              type={Text.Type.Title}
              text={formatCurrencyAmount(refundEstimate.totalEstimatedRefund, locale)}
            />
          }
        />
        {renderCurrencyConvertionNote()}
      </>
    )
  }

  const renderNote = () => {
    if (!note) return null

    return (
      <Cell
        styling={Cell.Styling.Tight}
        body={<Text as="h4" type={Text.Type.Caption} text={note} />}
      />
    )
  }

  const renderActions = () => (
    <Cell
      body={<Button text={translate('ok')} styling={Button.Styling.Filled} onClick={onClose} />}
    />
  )

  const renderContent = () =>
    isLoading ? (
      <ContentLoader styling={ContentLoader.Styling.Wide} />
    ) : (
      <div className="u-fill-width">
        {renderNavigation()}
        <Divider />
        <ScrollableArea>
          <Cell>
            <SeparatedList separator={<Spacer size={Spacer.Size.Large} />}>
              {renderRefundElements()}
              {renderTotalRefund()}
              {renderNote()}
            </SeparatedList>
          </Cell>
          {!isMobileView && renderActions()}
        </ScrollableArea>
      </div>
    )

  if (isMobileView)
    return (
      <BottomSheet isVisible={isOpen} onClose={onClose}>
        {renderContent()}
      </BottomSheet>
    )

  return (
    <Dialog hasScrollableContent show={isOpen} defaultCallback={onClose} closeOnOverlay>
      {renderContent()}
    </Dialog>
  )
}

export default BuyerRefundSummaryModal
